import { useState, useMemo, useEffect, useRef } from 'react';
import PostFilters from '@/components/posts/PostFilters';
import PostList from '@/components/posts/PostList';
import PublicAboutContent from '@/components/about/PublicAboutContent';
import type { Post } from '@/lib/firebase/posts';
// Removed membership tier features in favor of flexible donations
import type { UserData } from '@/lib/firebase/user';
import { applyAllFilters } from '@/lib/firebase/posts/utils';
import DonationForm from '@/components/DonationForm';
import DonationAbandonFeedback from '@/components/DonationAbandonFeedback';
import CreatorBadge from '@/components/mission/CreatorBadge';
import { Dialog } from '@headlessui/react';
import { useTranslation } from 'react-i18next';
import { subscribeToPrayerRequests as subscribeToStudyGoals } from '@/lib/firebase/prayer/queries';
import type { PrayerRequest as StudyGoal } from '@/lib/firebase/prayer/types';
import { trackDonateDialogOpened } from '@/lib/utils/analytics';

interface MissionContentProps {
  activeTab: string;
  posts: Post[];
  membershipTiers: any[];
  userData: UserData;
  isSubscribed: boolean;
  creatorId: string;
  creatorPath?: string; // Friendly URL path like "john-doe"
  givingEnabled?: boolean; // TSF-176: false = creator has no connected Stripe account
  creatorName?: string;
  creatorPhotoURL?: string;
}

export default function MissionContent({
  activeTab,
  posts,
  membershipTiers: _membershipTiers,
  userData,
  isSubscribed: _isSubscribed,
  creatorId,
  creatorPath,
  givingEnabled,
  creatorName,
  creatorPhotoURL,
}: MissionContentProps) {
  const { t } = useTranslation();
  const [showDonationForm, setShowDonationForm] = useState(false);
  const [showAbandonFeedback, setShowAbandonFeedback] = useState(false);
  const checkoutAttemptedRef = useRef(false);
  const dialogOpenedAtRef = useRef<number | null>(null);
  const defaultImpactText = 'Your contribution makes my sport possible. Every donation helps cover equipment, competition travel, coaching, and entry fees so I can focus on training and competing. Together we can go further — thank you for investing in my journey.';
  const [filters, setFilters] = useState<{
    tier?: string;
    dateRange?: { startDate: string; endDate: string };
    search?: string;
  }>({});

  // Study goals state
  const [studyGoals, setStudyGoals] = useState<StudyGoal[]>([]);

  if (!creatorId) {
    return null;
  }

  // Apply filters to posts
  const filteredPosts = useMemo(() => {
    return applyAllFilters(posts, filters);
  }, [posts, filters]);

  // Subscribe to study goals
  useEffect(() => {
    let unsub: (() => void) | null = null;
    try {
      unsub = subscribeToStudyGoals(creatorId, (items) => setStudyGoals(items));
    } catch (_) { }
    return () => { if (unsub) unsub(); };
  }, [creatorId]);

  useEffect(() => {
    // TSF: which CTA actually opens the dialog -- header vs. the mobile
    // sticky bar vs. an in-feed post -- was previously indistinguishable in
    // donate_dialog_opened, so a change to one surface couldn't be measured
    // against the others. Every dispatch site now passes its own name via
    // CustomEvent detail; a bare dispatch (tests, any stray caller) reads as
    // 'unknown' rather than silently losing the property.
    function handleOpenDonate(event: Event) {
      const surface = (event as CustomEvent<{ surface?: string }>).detail?.surface ?? 'unknown';
      checkoutAttemptedRef.current = false;
      dialogOpenedAtRef.current = Date.now();
      setShowDonationForm(true);
      trackDonateDialogOpened({ creatorId, surface });
      const donateSection = document.getElementById('donate');
      if (donateSection) {
        donateSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
    window.addEventListener('open-donate', handleOpenDonate as EventListener);
    return () => window.removeEventListener('open-donate', handleOpenDonate as EventListener);
  }, [creatorId]);

  // TSF-283: only a donor who opened the dialog, looked at the ask, and left
  // without ever reaching checkout is worth asking -- not a reflexive misclick
  // (ARM_DELAY below) and not someone who already redirected to Stripe.
  const ARM_DELAY_MS = 2000;
  function closeDonationDialog() {
    setShowDonationForm(false);
    const openedAt = dialogOpenedAtRef.current;
    if (!checkoutAttemptedRef.current && openedAt && Date.now() - openedAt > ARM_DELAY_MS) {
      setShowAbandonFeedback(true);
    }
  }

  // Public page: no management actions (even for owner)

  const renderContent = () => {
    switch (activeTab) {
      case '#home':
        return (
          <>
            <div id="donate" className="mb-12">
              <h2 className="text-2xl font-bold text-center mb-6">{t('creator.mypage.supportTitle')}</h2>
              <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-lg border border-indigo-100 p-6 text-gray-700 leading-relaxed">
                <p>
                  {(userData as any)?.donationImpact || defaultImpactText}
                </p>
              </div>
            </div>
            <PostFilters
              onFilterChange={setFilters}
            />
            <PostList
              posts={filteredPosts}
              userName={userData.displayName || ''}
              onPostUpdated={() => { }}
              isSubscribed={_isSubscribed}
              creatorId={creatorId}
            />
          </>
        );
      case '#about':
        return <PublicAboutContent userData={userData} sportingGoals={studyGoals} />;
      case '#donations':
        return (
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-center mb-8">
              My donations
            </h2>
            {/* TODO: List donor's recurring and one-off donations here */}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      {renderContent()}

      {/* Donation popup */}
      <Dialog open={showDonationForm} onClose={closeDonationDialog} className="relative z-[60]">
        <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <Dialog.Title className="text-xl font-bold text-gray-900 text-center">
              {t('creator.mypage.supportTitle')}
            </Dialog.Title>
            <CreatorBadge
              name={creatorName || userData?.displayName || ''}
              photoURL={creatorPhotoURL || userData?.photoURL}
              className="mt-2 mb-4"
            />
            <DonationForm
              missionId={creatorId}
              creatorPath={creatorPath}
              givingEnabled={givingEnabled}
              onSubmit={() => {}}
              onCheckoutAttempted={() => { checkoutAttemptedRef.current = true; }}
            />
            <button
              onClick={closeDonationDialog}
              className="mt-3 w-full text-sm text-gray-500 hover:text-gray-700 text-center"
            >
              {t('dashboard.common.cancel')}
            </button>
          </Dialog.Panel>
        </div>
      </Dialog>
      {showAbandonFeedback && (
        <DonationAbandonFeedback creatorId={creatorId} onDone={() => setShowAbandonFeedback(false)} />
      )}
    </div>
  );
}